'use client';

import { useEffect, useState } from 'react';

import posthog from 'posthog-js';
import { useFeatureFlagVariantKey } from 'posthog-js/react';

import type { FeatureFlagKeys, FeatureFlagVariant } from '../types';

function parseFeatureFlagVariant(value: string | boolean | undefined): FeatureFlagVariant | undefined {
  if (typeof value === 'string') {
    return value as FeatureFlagVariant;
  }

  return;
}

/**
 * Returns a multivariate flag variant only after PostHog has evaluated it.
 * Avoid defaulting to `control` while `undefined` — that causes analytics with the wrong variant.
 */
export function useGetFeatureFlagVariant<T extends FeatureFlagKeys>(
  featureFlag: T,
): {
  variant: FeatureFlagVariant | undefined;
  isReady: boolean;
} {
  const variantKey = useFeatureFlagVariantKey(featureFlag);
  const [loadedVariant, setLoadedVariant] = useState<FeatureFlagVariant | undefined>(() =>
    parseFeatureFlagVariant(posthog.getFeatureFlag(featureFlag) as string | boolean | undefined),
  );

  useEffect(() => {
    const syncVariant = (variants?: Record<string, string | boolean>) => {
      const fromCallback = parseFeatureFlagVariant(variants?.[featureFlag]);
      const fromClient = parseFeatureFlagVariant(posthog.getFeatureFlag(featureFlag) as string | boolean | undefined);

      setLoadedVariant(fromCallback ?? fromClient);
    };

    syncVariant(posthog.featureFlags?.getFlagVariants?.());

    const unsubscribe = posthog.onFeatureFlags((_flags, variants) => {
      syncVariant(variants);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [featureFlag]);

  const variant = parseFeatureFlagVariant(variantKey as string | boolean | undefined) ?? loadedVariant ?? undefined;

  return {
    variant,
    isReady: variant !== undefined,
  };
}
